'use client';

import { ShieldAlert } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';

export interface SessionExpiredNoticeProps {
    /** Whether the server session is considered dead. Nothing renders when false. */
    expired: boolean;
    /**
     * Invoked by the action button. Callers pass a reload: a fresh page load
     * with no valid session is precisely what re-runs the auth handoff, which
     * re-mints the cookie when the Firebase login is still good and otherwise
     * lands the user on the sign-in screen. Both are the right outcome, which
     * is why one button covers both.
     */
    onReload?: () => void;
    /** Overrides the default copy, for a surface that needs different wording. */
    message?: string;
}

export const SESSION_EXPIRED_TITLE = 'Your session has expired';

export const SESSION_EXPIRED_MESSAGE =
    'You are still signed in to this device, but the server no longer accepts this session — ' +
    'so anything you save will fail. Sign in again to continue.';

/**
 * Tells the operator, once, that their server session is dead.
 *
 * ## Why this exists
 *
 * The server session cookie and the Firebase sign-in expire on DIFFERENT
 * clocks. Firebase's refresh token effectively does not expire, while the
 * cookie tops out at 24h (14d with `rememberMe`). So a long-lived tab reaches a
 * state where the app looks perfectly signed in and every server write is
 * rejected.
 *
 * Nothing surfaced that. `useSessionKeepAlive` logged
 * `'Proactive session re-mint failed — will retry on next check'` to the
 * console and retried forever. The operator saw only the downstream symptoms:
 * in production on 2026-09-06, an admin save that spun on "Updating…"
 * indefinitely while the Firestore Write channel returned 400.
 *
 * This is deliberately NOT a toast. A toast for this is wrong twice over: it
 * disappears, and the condition does not — every subsequent action will fail
 * the same way until the session is repaired. It is also not dismissible, for
 * the same reason: dismissing it would hide a condition that is still true.
 *
 * Pure props, like {@link SystemHealthBanner} beside it — `packages/common`
 * cannot reach `packages/client`, so the state is resolved by the caller
 * (`AppProviders` via `useSessionHealth`) and passed in.
 */
export const SessionExpiredNotice = ({
    expired,
    onReload,
    message = SESSION_EXPIRED_MESSAGE,
}: SessionExpiredNoticeProps) => {
    if (!expired) return null;

    return (
        <div className="fixed inset-x-0 top-0 z-[100] p-3">
            <Alert
                variant="destructive"
                // `assertive`: this is not ambient status — the user's next
                // action will fail, so it should interrupt a screen reader
                // rather than wait for a pause.
                role="alert"
                aria-live="assertive"
                className="mx-auto flex max-w-3xl items-start gap-3 shadow-lg"
            >
                <ShieldAlert className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
                <div className="flex-1">
                    <AlertTitle>{SESSION_EXPIRED_TITLE}</AlertTitle>
                    <AlertDescription>{message}</AlertDescription>
                </div>
                {onReload ? (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onReload}
                        className="shrink-0"
                    >
                        Reload
                    </Button>
                ) : null}
            </Alert>
        </div>
    );
};
